import { CustomerStatus } from '@api/hooks/useProfiles';
import { Skeleton } from '@components/skeleton/Skeleton';
import { useBankProfile } from '@contexts/BankProfileContext.tsx';
import { Alert, AlertType, Collapsible, Typography } from '@gurupay/gp-ui-library';
import { StatusGrid } from '@pages/dashboard/components/StatusWidget/StatusGrid.tsx';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

const statusLabel: Record<CustomerStatus | 'actions', string> = {
	[CustomerStatus.OPEN]: 'status.active',
	[CustomerStatus.DORMANT]: 'status.inactive',
	[CustomerStatus.BLOCKED]: 'status.blocked',
	[CustomerStatus.ABANDONED]: 'status.abandoned',
	[CustomerStatus.CLOSED]: 'status.closed',
	[CustomerStatus.PENDING]: 'status.pending',
	actions: 'status.actionRequired',
};

export const StatusWidget = () => {
	const { t } = useTranslation('', { keyPrefix: 'statusWidget' });
	const { selectedProfile } = useBankProfile();
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });

	return (
		<div className="max-mobile:p-space-16 p-space-24 bg-white rounded-md space-y-space-16 flex flex-col justify-between h-full">
			<div className="space-y-space-16">
				<div className="flex w-full items-center justify-between">
					<Typography className="font-medium text-neutral-800" level="small-text">
						{t('title')}
					</Typography>
					<Link
						className="text-sm-bold text-brand-secondary-600 hover:text-neutral-950 border-b border-b-transparent hover:border-b-neutral-950 transition-all duration-300"
						to="/settings/profile"
					>
						{t('viewSettings')}
					</Link>
				</div>
				{isMobile ? (
					<Collapsible
						className="border-none"
						iconPosition="right"
						title={
							<div
								className={clsx(
									'max-mobile:text-[24px] font-bold leading-7.5 whitespace-nowrap text-ellipsis overflow-hidden',
									!!selectedProfile?.odd_eligible && 'text-status-warning-500',
									(selectedProfile?.status ?? CustomerStatus.OPEN) ===
										CustomerStatus.OPEN && 'text-status-success-500',
								)}
							>
								{!selectedProfile ? (
									<Skeleton className="w-full h-13.5 max-mobile:h-9" />
								) : (
									t(
										selectedProfile?.odd_eligible
											? statusLabel.actions
											: statusLabel[selectedProfile?.status ?? CustomerStatus.OPEN],
									)
								)}
							</div>
						}
					>
						<StatusGrid />
					</Collapsible>
				) : (
					<>
						<div
							className={clsx(
								'max-mobile:text-[24px] text-[36px] font-bold leading-1.2 mt-space-12 whitespace-nowrap text-ellipsis overflow-x-hidden',
								!!selectedProfile?.odd_eligible && 'text-status-warning-500',
								(selectedProfile?.status ?? CustomerStatus.OPEN) ===
									CustomerStatus.OPEN && 'text-status-success-500',
							)}
						>
							{!selectedProfile ? (
								<Skeleton className="w-full h-13.5 max-mobile:h-9" />
							) : (
								t(
									selectedProfile?.odd_eligible
										? statusLabel.actions
										: statusLabel[selectedProfile?.status ?? CustomerStatus.OPEN],
								)
							)}
						</div>
						<StatusGrid />
					</>
				)}
			</div>
			{!!selectedProfile && (
				<Alert
					className="w-full!"
					title={t(
						selectedProfile?.odd_eligible
							? 'alert.actionRequired.title'
							: 'alert.noActionRequired.title',
					)}
					type={selectedProfile?.odd_eligible ? AlertType.WARNING : AlertType.SUCCESS}
				>
					{t(
						selectedProfile?.odd_eligible
							? 'alert.actionRequired.description'
							: 'alert.noActionRequired.description',
					)}
				</Alert>
			)}
		</div>
	);
};
